/**
 * Производные карточки хозяйства.
 *
 * Массив собственным полем `/actors/{id}` не отдаёт, и это правильно: земля
 * хозяйства может лежать в нескольких массивах, и одно поле пришлось бы
 * выбирать за человека. Сервер называет массив у КАЖДОГО землепользования
 * (`land_uses[].massif`), поэтому ступень цепочки выводится здесь — в
 * проверяемой функции, а не в разметке, где «взять первый» выглядит как факт.
 */

import type { ActorLandUseOut } from "../../api/client";
import type { Message } from "../../i18n/define";
import { tr } from "../../i18n/runtime";

/**
 * Три формы существительного для компонентов, которые ещё принимают кортеж
 * «1 / 2 / 5»: берутся из счётного сообщения каталога (`WORDS.*`) на языке
 * интерфейса, число отбрасывается. Зовётся при рендере, не при импорте.
 */
export function nounForms(message: Message): [string, string, string] {
  const word = (count: number) =>
    tr(message as Message<"common.count.contour">, { count }).replace(/^\S+\s+/, "");
  return [word(1), word(2), word(5)];
}

/** Ссылка на массив в том виде, в каком её отдаёт землепользование. */
export type ActorMassif = ActorLandUseOut["massif"];

/**
 * Различные массивы, в которых лежит земля хозяйства.
 *
 * Порядок — по имени, а не по порядку строк выдачи: цепочка не должна
 * меняться от того, какое землепользование сервер вернул первым.
 */
export function actorMassifs(
  landUses: readonly ActorLandUseOut[],
): ActorMassif[] {
  const byId = new Map<number, ActorMassif>();
  for (const use of landUses) {
    if (!byId.has(use.massif.id)) byId.set(use.massif.id, use.massif);
  }
  return [...byId.values()].sort((first, second) =>
    first.canonical_name.localeCompare(second.canonical_name, "ru"),
  );
}

/** Земля хозяйства в одном массиве: чем эта территория для него является. */
export interface ActorMassifLoad {
  massif: ActorMassif;
  /** Различные контуры хозяйства на этой территории. */
  contours: number;
  /** Сумма площадей строк, у которых площадь есть. `null` — её нет ни у одной. */
  totalMicroha: number | null;
  /** Строк без величины. Их площадь НЕ считается нулём. */
  missingRows: number;
}

/**
 * Разложить землю хозяйства по массивам.
 *
 * Складывать строки землепользования между собой можно: каждая несёт площадь
 * СВОЕГО зерна (часть — площадь части, контур — площадь контура), и одна и та
 * же земля входит в сумму один раз. Строка без величины в сумму не входит и не
 * превращается в ноль — она считается отдельно и называется вслух.
 *
 * Порядок — по имени массива: он не должен меняться от того, какое
 * землепользование сервер вернул первым.
 */
export function actorMassifLoad(
  landUses: readonly ActorLandUseOut[],
): ActorMassifLoad[] {
  const byId = new Map<number, ActorMassifLoad & { seen: Set<number> }>();

  for (const use of landUses) {
    const current = byId.get(use.massif.id) ?? {
      massif: use.massif,
      contours: 0,
      totalMicroha: null,
      missingRows: 0,
      seen: new Set<number>(),
    };
    if (!current.seen.has(use.contour_id)) {
      current.seen.add(use.contour_id);
      current.contours += 1;
    }
    if (use.total_microha === null) current.missingRows += 1;
    else current.totalMicroha = (current.totalMicroha ?? 0) + use.total_microha;
    byId.set(use.massif.id, current);
  }

  return [...byId.values()]
    .map(({ seen: _seen, ...load }) => load)
    .sort((first, second) =>
      first.massif.canonical_name.localeCompare(
        second.massif.canonical_name,
        "ru",
      ),
    );
}
